import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import BookButton from "@/components/BookButton";
import SocialProof from "@/components/SocialProof";
import AudienceCards from "@/components/AudienceCards";
import StoryStrip from "@/components/StoryStrip";
import CelebrateSection from "@/components/CelebrateSection";
import TrustBadges from "@/components/TrustBadges";
import CitiesAndTopics from "@/components/CitiesAndTopics";
import { HERO_IMAGES, PROPERTY } from "@/lib/constants";
import { useSEO } from "@/lib/seo";

const AMENITY_ICONS = {
  coffee: "M3 11h14v4a4 4 0 01-4 4H7a4 4 0 01-4-4v-4zm14 0h2a2 2 0 010 4h-2M5 7c0-1 .5-2 2-2s2 1 2 2-.5 2-2 2-2-1-2-2zm6 0c0-1 .5-2 2-2s2 1 2 2-.5 2-2 2-2-1-2-2z",
  wifi: "M5 13a10 10 0 0114 0M8.5 16.5a5 5 0 017 0M12 20h.01",
  paw: "M5 12c1.5 0 2.5-1.5 2.5-3S6.5 6 5 6s-2.5 1.5-2.5 3S3.5 12 5 12zm14 0c1.5 0 2.5-1.5 2.5-3S20.5 6 19 6s-2.5 1.5-2.5 3S17.5 12 19 12zM9 7c1 0 2-1.3 2-3s-1-3-2-3-2 1.3-2 3 1 3 2 3zm6 0c1 0 2-1.3 2-3s-1-3-2-3-2 1.3-2 3 1 3 2 3zm-3 15c4 0 6-2 6-5 0-3-3-4-6-4s-6 1-6 4 2 5 6 5z",
  truck: "M3 7h11v8H3zM14 10h4l3 3v2h-7zM7 19a2 2 0 100-4 2 2 0 000 4zm10 0a2 2 0 100-4 2 2 0 000 4z",
  phone: "M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.13.96.36 1.9.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0122 16.92z",
  fridge: "M5 3h14v18H5zM5 11h14M9 7v.01M9 15v.01",
  bed: "M3 18v-7a3 3 0 013-3h12a3 3 0 013 3v7M3 18h18M3 14h18M9 11h6",
  clock: "M12 8v4l3 2M12 22a10 10 0 100-20 10 10 0 000 20z",
  sparkle: "M12 2l2.39 5.18L20 8.27l-4 3.9.94 5.49L12 15.77l-4.94 1.89L8 12.17l-4-3.9 5.61-1.09L12 2z",
};

function Icon({ name }) {
  const d = AMENITY_ICONS[name] || AMENITY_ICONS.sparkle;
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={d} />
    </svg>
  );
}

export default function Home() {
  const [rooms, setRooms] = useState([]);
  const [amenities, setAmenities] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [attractions, setAttractions] = useState([]);
  const [posts, setPosts] = useState([]);
  const [heroIdx, setHeroIdx] = useState(0);

  useSEO({
    title: "Hotel in Carrollton, Missouri · Pet Friendly · US-65",
    description:
      "Westbridge Inn & Suites — independent hotel on US Highway 65 in Carrollton, Missouri. 34 rooms including a jacuzzi suite and three-bedroom family suite. Free hot breakfast, pet friendly, truck parking, kids under 18 stay free. Book direct & save.",
    path: "/",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Hotel",
      name: "Westbridge Inn & Suites",
      url: "https://www.westbridgecarrollton.com/",
      telephone: "+1-660-542-2988",
      email: "carrollton@westbridgehotelgroup.com",
      image: "https://www.westbridgecarrollton.com/images/property/exterior-main.jpg",
      priceRange: "$$",
      checkinTime: "15:00",
      checkoutTime: "11:00",
      numberOfRooms: 34,
      address: {
        "@type": "PostalAddress",
        streetAddress: "1408 N US-65",
        addressLocality: "Carrollton",
        addressRegion: "MO",
        postalCode: "64633",
        addressCountry: "US",
      },
      geo: { "@type": "GeoCoordinates", latitude: 39.3624, longitude: -93.4969 },
      petsAllowed: true,
      amenityFeature: [
        { "@type": "LocationFeatureSpecification", name: "Free Wi-Fi", value: true },
        { "@type": "LocationFeatureSpecification", name: "Free hot breakfast", value: true },
        { "@type": "LocationFeatureSpecification", name: "Pet friendly", value: true },
        { "@type": "LocationFeatureSpecification", name: "Truck and bus parking", value: true },
        { "@type": "LocationFeatureSpecification", name: "In-room jacuzzi suite", value: true },
        { "@type": "LocationFeatureSpecification", name: "Three-bedroom family suite", value: true },
        { "@type": "LocationFeatureSpecification", name: "24-hour front desk", value: true },
      ],
    },
  });

  useEffect(() => {
    Promise.all([
      api.get("/rooms").then((r) => setRooms(r.data)).catch(() => {}),
      api.get("/amenities").then((r) => setAmenities(r.data)).catch(() => {}),
      api.get("/testimonials").then((r) => setTestimonials(r.data)).catch(() => {}),
      api.get("/attractions").then((r) => setAttractions(r.data)).catch(() => {}),
      api.get("/blog").then((r) => setPosts(r.data.slice(0, 3))).catch(() => {}),
    ]);
  }, []);

  useEffect(() => {
    const t = setInterval(() => setHeroIdx((i) => (i + 1) % HERO_IMAGES.length), 6500);
    return () => clearInterval(t);
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[100vh] flex items-end overflow-hidden bg-ink">
        {HERO_IMAGES.map((src, i) => (
          <div
            key={src}
            className="absolute inset-0 transition-opacity duration-[1800ms] ease-in-out"
            style={{ opacity: i === heroIdx ? 1 : 0 }}
          >
            <img
              src={src}
              alt=""
              className="w-full h-full object-cover"
              loading={i === 0 ? "eager" : "lazy"}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-ink/20" />
          </div>
        ))}

        <div className="relative max-w-[1400px] w-full mx-auto px-6 md:px-10 pb-20 md:pb-28 pt-32 z-10">
          <div className="grid md:grid-cols-12 gap-8 items-end">
            <div className="md:col-span-8">
              <p className="font-mono-label text-canvas/80 mb-6" data-testid="hero-eyebrow">
                Carrollton, Missouri · Established on US Highway 65
              </p>
              <h1 className="font-display font-extrabold text-canvas text-[3rem] sm:text-[3.9rem] md:text-[5rem] leading-[1.0] tracking-tight" data-testid="hero-headline">
                Pull off the highway.<br />
                <span className="italic text-ember">Stay a while.</span>
              </h1>
              <p className="mt-8 text-canvas/85 text-lg md:text-xl max-w-xl leading-relaxed">
                An independent inn on the quiet middle of Missouri — thirty-four freshly kept rooms,
                from king suites and an in-room jacuzzi to a three-bedroom family suite, plus a proper
                hot breakfast and a small team that answers the phone. The kind of place road-weary
                families, working crews and weekend wanderers actually look forward to.
              </p>
              <div className="mt-10 flex flex-wrap items-center gap-4" data-testid="hero-ctas">
                <BookButton label="Book Direct & Save" testid="hero-book-btn" />
                <Link to="/accommodations" className="btn-ink" data-testid="hero-rooms-btn">
                  Explore Rooms
                </Link>
              </div>
            </div>
            <div className="md:col-span-4 hidden md:block">
              <div className="bg-canvas/95 backdrop-blur p-8 border-l-2 border-ember">
                <p className="font-mono-label text-ink-muted mb-3">Reservations · 24 hours</p>
                <a
                  href={`tel:${PROPERTY.phone.replace(/[^\d+]/g, "")}`}
                  className="font-display text-5xl text-ink hover:text-ember transition-colors"
                  data-testid="hero-phone"
                >
                  {PROPERTY.phone}
                </a>
                <div className="hairline my-6" />
                <p className="text-ink-soft text-sm leading-relaxed">
                  {PROPERTY.street}<br />
                  {PROPERTY.cityState}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* dots */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 z-10" aria-hidden="true">
          {HERO_IMAGES.map((_, i) => (
            <span
              key={i}
              className={`h-[2px] transition-all duration-500 ${i === heroIdx ? "w-10 bg-ember" : "w-5 bg-canvas/40"}`}
            />
          ))}
        </div>
      </section>

      {/* TRUST MARQUEE */}
      <section className="bg-ink text-canvas overflow-hidden py-6 border-y border-canvas/15" aria-label="At-a-glance highlights">
        <div className="scroll-marquee flex gap-16 whitespace-nowrap font-mono-label text-canvas/70">
          {[
            "34 Rooms · 6 Room Types",
            "Free Deluxe Hot Breakfast",
            "Pet Friendly · 2 pets per room",
            "Truck & Bus Parking",
            "24-Hour Front Desk",
            "Kids Under 18 Stay Free",
            "In-Room Jacuzzi Suite",
            "Book Direct — No Junk Fees",
          ].concat([
            "34 Rooms · 6 Room Types",
            "Free Deluxe Hot Breakfast",
            "Pet Friendly · 2 pets per room",
            "Truck & Bus Parking",
            "24-Hour Front Desk",
            "Kids Under 18 Stay Free",
            "In-Room Jacuzzi Suite",
            "Book Direct — No Junk Fees",
          ]).map((t, i) => (
            <span key={i} className="flex items-center gap-16">
              {t}
              <span className="text-ember">✦</span>
            </span>
          ))}
        </div>
      </section>

      {/* SOCIAL PROOF / REVIEWS */}
      <SocialProof variant="full" />

      {/* TRUST BADGES */}
      <TrustBadges />

      {/* WHO IT'S FOR — AUDIENCE CARDS */}
      <AudienceCards />

      {/* WELCOME / STORY */}
      <section className="bg-canvas py-24 md:py-36">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-16 items-start">
          <div className="md:col-span-5 md:sticky md:top-32">
            <p className="font-mono-label text-ember mb-6">Welcome to Carrollton</p>
            <h2 className="font-display text-5xl md:text-6xl leading-[1.02] text-ink">
              An independent inn,<br />
              <span className="italic">run by people who answer the phone.</span>
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7 space-y-6 text-lg leading-relaxed text-ink-soft">
            <p>
              Westbridge Inn &amp; Suites sits right off US Highway 65 — close enough that you'll find us
              from the road, quiet enough that you'll actually sleep once you do. Thirty-four rooms,
              a dedicated team trained in hospitality, and a clear commitment: clean, comfortable,
              fairly priced.
            </p>
            <p>
              We're a short drive from Van Meter State Park, Swan Lake National Wildlife Refuge, the
              village of Arrow Rock, and the famously oversized Pecan in Brunswick. Pizza Hut and a
              fuel stop are within walking distance — handy when you've checked in late and the only
              thing you want is a hot bite and a quiet room.
            </p>
            <p data-testid="brand-meaning">
              {"The name is the whole idea. A bridge is what carries you over the long middle of a journey — and out here on US-65, that's what we are: the bridge between the road and a good night's rest. It's why the sign reads Westbridge, and why the promise under it hasn't changed since it first lit up over the highway: great service, clean rooms. Thank you for letting us be part of your journey — it's an honor we work to earn every night. "}
              <Link to="/about" className="text-ember underline underline-offset-4 decoration-1 hover:text-ember-hover" data-testid="welcome-about-link">Read our story →</Link>
            </p>
            <div className="pt-6 flex flex-wrap gap-x-12 gap-y-8" data-testid="story-stats">
              {[
                ["34", "guest rooms"],
                ["6", "room types"],
                ["4.6★", "Google rating"],
                ["24/7", "front desk"],
              ].map(([n, l]) => (
                <div key={l}>
                  <p className="font-display text-6xl md:text-7xl text-ember leading-none">{n}</p>
                  <p className="font-mono-label text-ink-muted mt-2">{l}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* AMENITIES BENTO */}
      <section className="bg-sand py-24 md:py-32 relative" data-testid="amenities-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
            <div>
              <p className="font-mono-label text-ember mb-4">What's included</p>
              <h2 className="font-display text-5xl md:text-6xl text-ink leading-tight max-w-2xl">
                Comforts that<br />actually count.
              </h2>
            </div>
            <Link to="/amenities" className="link-underline font-mono-label text-ink-soft" data-testid="amenities-see-all">
              See full amenities &amp; policies →
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-rule-strong">
            {amenities.slice(0, 8).map((a, i) => (
              <div
                key={a.id}
                className="bg-canvas p-8 md:p-10 group hover:bg-ember-light transition-colors duration-300 fade-up"
                style={{ animationDelay: `${i * 60}ms` }}
                data-testid={`amenity-card-${i}`}
              >
                <div className="text-ember mb-6 group-hover:scale-110 transition-transform">
                  <Icon name={a.icon} />
                </div>
                <h3 className="font-display text-2xl text-ink mb-3 leading-tight">{a.title}</h3>
                <p className="text-ink-soft text-sm leading-relaxed">{a.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ROOMS PREVIEW */}
      <section className="bg-canvas py-24 md:py-36" data-testid="rooms-preview-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid md:grid-cols-12 gap-8 mb-16 items-end">
            <div className="md:col-span-7">
              <p className="font-mono-label text-ember mb-4">Accommodations</p>
              <h2 className="font-display text-5xl md:text-6xl text-ink leading-[1.02]">
                Choose your room.<br />
                <span className="italic">Sleep on it.</span>
              </h2>
            </div>
            <div className="md:col-span-5 md:text-right">
              <Link to="/accommodations" className="link-underline font-mono-label text-ink-soft" data-testid="rooms-see-all">
                View all 6 room types →
              </Link>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-10">
            {rooms.slice(0, 3).map((room, i) => (
              <Link
                to="/accommodations"
                key={room.id}
                className="group block fade-up"
                style={{ animationDelay: `${i * 80}ms` }}
                data-testid={`room-card-${room.slug}`}
              >
                <div className="aspect-[4/5] overflow-hidden bg-dune mb-5">
                  <img
                    src={room.image_url}
                    alt={`${room.name} at Westbridge Inn & Suites`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1200ms]"
                    loading="lazy"
                  />
                </div>
                <div className="flex items-baseline justify-between gap-4">
                  <h3 className="font-display text-3xl text-ink">{room.name}</h3>
                  {room.price_from && (
                    <span className="font-mono-label text-ember">from ${room.price_from}/nt</span>
                  )}
                </div>
                <p className="font-mono-label text-ink-muted mt-2">{room.bed_config} · sleeps {room.max_guests}</p>
                <p className="text-ink-soft mt-3 leading-relaxed line-clamp-3">{room.description}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CELEBRATE — Jacuzzi Suite conversion block */}
      <CelebrateSection />

      {/* TESTIMONIALS */}
      <section className="bg-ink text-canvas py-24 md:py-36 relative overflow-hidden" data-testid="testimonials-section">
        <div className="grain" />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
          <p className="font-mono-label text-ember mb-4">From the guestbook</p>
          <h2 className="font-display text-5xl md:text-6xl text-canvas mb-16 max-w-3xl leading-[1.02]">
            What returning guests<br /><span className="italic">keep telling us.</span>
          </h2>
          <div className="grid md:grid-cols-2 gap-12">
            {testimonials.slice(0, 2).map((t, i) => (
              <figure key={t.id} className="fade-up" style={{ animationDelay: `${i * 100}ms` }} data-testid={`testimonial-${i}`}>
                <div className="quote-mark mb-2">“</div>
                <blockquote className="font-display text-2xl md:text-3xl leading-snug text-canvas/95">
                  {t.quote}
                </blockquote>
                <figcaption className="mt-6 font-mono-label text-canvas/60">
                  — {t.author} · {t.source}
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS section already exists; insert StoryStrip after testimonials */}
      <StoryStrip />

      {/* JOURNAL / BLOG TEASER */}
      <section className="bg-canvas py-24 md:py-32 border-t border-rule-subtle" data-testid="journal-teaser-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <p className="font-mono-label text-ember mb-4">From the journal</p>
              <h2 className="font-display text-5xl md:text-6xl text-ink leading-[1.02] max-w-2xl">
                Practical reading,<br /><span className="italic">written by us.</span>
              </h2>
            </div>
            <Link to="/blog" className="link-underline font-mono-label text-ink-soft" data-testid="journal-see-all">
              All articles →
            </Link>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {posts.map((p, i) => (
              <Link
                key={p.id}
                to={`/blog/${p.slug}`}
                className="group block fade-up"
                style={{ animationDelay: `${i * 80}ms` }}
                data-testid={`journal-card-${p.slug}`}
              >
                <div className="aspect-[4/3] overflow-hidden bg-dune mb-5">
                  <img src={p.hero_image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1200ms]" loading="lazy" />
                </div>
                <p className="font-mono-label text-ember">{p.category}</p>
                <h3 className="font-display text-2xl text-ink mt-2 leading-tight">{p.title}</h3>
                <p className="text-ink-soft mt-3 line-clamp-2 leading-relaxed">{p.excerpt}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* LOCAL GUIDE TEASER */}
      <section className="bg-canvas py-24 md:py-36" data-testid="local-teaser-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <p className="font-mono-label text-ember mb-4">Hyper-local guide</p>
              <h2 className="font-display text-5xl md:text-6xl text-ink leading-[1.02] max-w-2xl">
                A region<br />worth lingering in.
              </h2>
            </div>
            <Link to="/local-guide" className="link-underline font-mono-label text-ink-soft" data-testid="local-see-all">
              Full Carrollton guide →
            </Link>
          </div>

          <div className="grid md:grid-cols-12 gap-4 md:gap-6">
            {attractions.filter((a) => a.image_url).slice(0, 4).map((a, i) => (
              <div
                key={a.id}
                className={`relative group overflow-hidden bg-dune fade-up ${
                  i === 0 ? "md:col-span-7 md:row-span-2 aspect-[4/3] md:aspect-[16/12]" :
                  i === 1 ? "md:col-span-5 aspect-[4/3]" :
                  "md:col-span-6 aspect-[4/3]"
                }`}
                style={{ animationDelay: `${i * 80}ms` }}
                data-testid={`local-card-${i}`}
              >
                <img src={a.image_url} alt={a.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1500ms]" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/30 to-transparent" />
                <div className="absolute inset-0 p-6 md:p-8 flex flex-col justify-end">
                  <p className="font-mono-label text-canvas/70 mb-1">{a.category} · {a.distance}</p>
                  <h3 className="font-display text-3xl md:text-4xl text-canvas leading-tight">{a.name}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CITIES & TOPICS — comprehensive interlinking for SEO */}
      <CitiesAndTopics />

      {/* CTA FOOTER STRIP */}
      <section className="bg-ember relative overflow-hidden" data-testid="cta-strip">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-24 grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <h2 className="font-display text-5xl md:text-6xl text-canvas leading-[1.02]">
              Reserve your room — direct.
            </h2>
            <p className="mt-6 text-canvas/90 text-lg max-w-xl">
              Best rates are always on this website. No third-party fees, no surprises at the front desk.
            </p>
          </div>
          <div className="md:col-span-4 md:text-right">
            <div className="inline-flex flex-col items-start md:items-end gap-4">
              <BookButton label="Book Direct & Save" variant="ink" className="!border-canvas !text-canvas hover:!bg-canvas hover:!text-ember" testid="cta-strip-book" />
              <a href={`tel:${PROPERTY.phone.replace(/[^\d+]/g, "")}`} className="link-underline text-canvas font-mono-label" data-testid="cta-strip-phone">
                or call {PROPERTY.phone}
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
